import { create } from './create';
import { serialize } from './serialize';
import { Constructor, Data } from './types';

type BuildOptions<T extends Data> = string | Partial<{ [k in keyof T]: any }>;

class BuildResult<T> {
	private _json: any;

	constructor(public schema: T) {}

	get json() {
		if (!this._json) {
			this._json = serialize(this.schema as any);
		}

		return this._json;
	}
}

type FieldDefType = 'raw' | 'function' | 'defer' | 'builder';
type FieldDef = { type: FieldDefType; source: any; value?: any };

export function defer<T extends Data>(generator: (record: T) => any = Number): FieldDef {
	return { type: 'defer', source: generator };
}

let n = 1;

export function inc(generator: (n: number) => any) {
	return () => generator(n++);
}

export function use(builder: Builder | [Builder], count?: number): FieldDef {
	if (typeof count === 'number') {
		if (count < 1) {
			throw new Error('A count of at least 1 must be provided');
		}

		if (!Array.isArray(builder)) {
			throw new Error(`An array type builder must be provided: ${builder}`);
		}
	}

	return { type: 'builder', source: builder, value: count };
}

function isFieldDef(def: any): def is FieldDef {
	return (
		!!def &&
		typeof def === 'object' &&
		'type' in def &&
		typeof def.type === 'string' &&
		['raw', 'function', 'defer', 'builder'].includes(def.type) &&
		'source' in def
	);
}

// type FieldDefs<T> = { [K in keyof T]: T[K] | (() => T[K]) };

export class Builder<T extends Data = Data> {
	private traitTypes: { [k: string]: Data } = {};
	private defaultFieldDefs: { [k: string]: FieldDef } = {};

	constructor(
		private schema: Constructor<T>,
		defaultFields: Data = {}
	) {
		this.validateDefaults(defaultFields);
		this.defaultFieldDefs = this.buildFieldDefs(defaultFields);
	}

	as(name: string, data: any) {
		this.traitTypes[name] = data;
		return this;
	}

	build(count: number, ...traitsOrData: BuildOptions<T>[]): BuildResult<T[]>;
	build(...traitsOrData: BuildOptions<T>[]): BuildResult<T>;
	build(...countTraitsOrData: any): any {
		const { count, traits, data } = this.validateAndParseOptions(countTraitsOrData);

		// Combine the data passed in to the `build()` call with that provided by any
		// specified traits
		const resolvedData = this.mergeData(data, traits);

		if (count === undefined) {
			return new BuildResult(this.construct(resolvedData));
		}

		const records = Array.from({ length: count as number }, () => this.construct(resolvedData));

		return new BuildResult(records);
	}

	private construct(data: Data): T {
		const createData: Data = {};

		// First, iterate over all of the data the user passed in via traits or directly, which
		// takes precedent over any default data
		// TODO: Clean all of this up
		for (const [field, value] of Object.entries(data)) {
			const def = this.defaultFieldDefs[field];

			if (!def || def.type === 'raw' || def.type === 'function') {
				createData[field] = typeof value === 'function' ? value() : value;
				continue;
			}

			if (def.type === 'builder') {
				if (Array.isArray(def.source)) {
					const [builder] = def.source;

					if (typeof value === 'number') {
						createData[field] = Array.from({ length: value }, () => builder.build().schema);
					} else if (Array.isArray(value)) {
						createData[field] = value.map((data) => builder.build(data).schema);
					}
				} else {
					if (typeof value === 'number') {
						throw new Error(`Attempted to generate multiple records for a non-many association: ${field}`);
					} else {
						createData[field] = value === null ? null : def.source.build(value ?? {}).schema;
					}
				}
			}
		}

		// Iterate over the defaults and fill in any field that hasn't been specifically set
		for (const [field, def] of Object.entries(this.defaultFieldDefs)) {
			// Be sure to skip any deferred fields
			if (field in createData || Object.keys(data).includes(field)) {
				continue;
			}

			switch (def.type) {
				case 'builder':
					if (Array.isArray(def.source)) {
						const [builder] = def.source;
						const count = def.value ?? 1;

						createData[field] = Array.from({ length: count }, () => builder.build().schema);
					} else {
						createData[field] = def.source.build().schema;
					}
					break;

				case 'raw':
					createData[field] = def.source;
					break;

				case 'function':
					createData[field] = def.source();
					break;
			}
		}

		// Create a manually passed defer
		for (const [field, def] of Object.entries(data).filter(([, def]) => def && typeof def === 'object' && def.type === 'defer')) {
			createData[field] = def.source(createData);
		}

		// Only create a default defer if it hasn't been manually passed
		for (const [field, def] of Object.entries(this.defaultFieldDefs).filter(([, def]) => def.type === 'defer')) {
			if (field in createData) {
				continue;
			}

			createData[field] = def.source(createData);
		}

		return create(this.schema, createData);
	}

	private mergeData(data: Data, traits: string[]) {
		return traits.reduce((all, trait) => {
			return {
				...all,
				...(this.traitTypes[trait] || {}),
			};
		}, data);
	}

	private buildFieldDefs(data: Data): { [k: string]: FieldDef } {
		return Object.fromEntries(
			Object.entries(data).map(([fieldName, source]) => {
				if (isFieldDef(source)) {
					return [fieldName, source];
				}

				if (typeof source === 'function') {
					return [fieldName, { type: 'function', source }];
				}

				return [fieldName, { type: 'raw', source }];
			})
		);
	}

	private traitNames() {
		return Object.keys(this.traitTypes);
	}

	private traitExists(name: string) {
		return this.traitNames().includes(name);
	}

	private validateAndParseOptions(data: (number | BuildOptions<T>)[]) {
		const totalCounts = data.filter((d) => typeof d === 'number');

		if (totalCounts.length > 1) {
			throw new Error('Invalid build options: Only one count can be specified');
		}

		const countIndex = data.findIndex((d) => typeof d === 'number');

		if (countIndex > 0) {
			throw new Error('Invalid build options: The count must be specified first');
		}

		const count = totalCounts[0];

		if (count !== undefined && typeof count === 'number' && count < 1) {
			throw new Error('Invalid build options: The count must be at least 1');
		}

		const totalData = data.filter((d) => typeof d === 'object');

		if (totalData.length > 1) {
			throw new Error('Invalid build options: Only one data object can be specified');
		}

		const dataIndex = data.findIndex((d) => typeof d === 'object');

		// If the data object is not the last in the array, throw an error
		if (dataIndex !== -1 && dataIndex !== data.length - 1) {
			throw new Error('Invalid build options: The data object must be specified last');
		}

		const traits = data.filter((d) => typeof d === 'string') as string[];

		traits.forEach((trait) => {
			if (!this.traitExists(trait)) {
				throw new Error(`Trait ${trait} does not exist`);
			}
		});

		return {
			count,
			traits,
			data: totalData[0] || {},
		};
	}

	private validateDefaults(data: Data) {
		for (const [key, value] of Object.entries(data)) {
			if ((value !== null && typeof value === 'object' && !isFieldDef(value)) || Array.isArray(value)) {
				throw new Error(`Collection data value (${typeof value}) for field ${key} must be returned by a function`);
			}
		}
	}
}
